import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { ReportShell } from "@/features/reports/ReportShell";
import { KpiCard } from "@/features/reports/KpiCard";
import { useQuery } from "@tanstack/react-query";
import { apiList } from "@/integrations/api/client";
import { usePmKpis } from "@/features/pm/api";
import { useAssetValueKpis, useLicencesExpiringKpi, useContractsExpiringKpi, useCalibrationDueKpi } from "@/features/reports/kpis";
import type { ReportFilters } from "@/features/reports/types";
import { useBranch } from "@/context/BranchContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isBranchLocked } from "@/lib/roles";

export const Route = createFileRoute("/_authenticated/reports/nabh-evidence")({
  component: NabhPack,
});

function NabhPack() {
  const [filters, setFilters] = useState<ReportFilters>({});
  const { currentBranchId } = useBranch();
  const { data: me } = useCurrentUser();
  const lockedBranchId = isBranchLocked(me?.roles) ? (me?.profile?.branch_id ?? null) : null;
  const effectiveBranchId = lockedBranchId ?? filters.branchId ?? currentBranchId ?? null;
  const { data: pm } = usePmKpis(effectiveBranchId);
  const { data: av } = useAssetValueKpis(effectiveBranchId);
  const { data: licExp } = useLicencesExpiringKpi(90, effectiveBranchId);
  const { data: amcExp } = useContractsExpiringKpi(90, effectiveBranchId);
  const { data: calDue } = useCalibrationDueKpi(60, effectiveBranchId);
  const { data: audits } = useQuery({
    queryKey: ["rep_nabh_audits", effectiveBranchId],
    queryFn: async () => {
      try {
        const p1: any = { status_in: "Completed,Signed Off", limit: 1 };
        const p2: any = { status: "Signed Off", limit: 1 };
        if (effectiveBranchId) { p1.branch = effectiveBranchId; p2.branch = effectiveBranchId; }
        
        const [r1, r2] = await Promise.all([
          apiList("/api/v1/audits/", p1),
          apiList("/api/v1/audits/", p2)
        ]);
        return { completed: r1.count ?? 0, signed: r2.count ?? 0 };
      } catch (e) {
        return { completed: 0, signed: 0 };
      }
    },
  });

  const { data: lifecycle } = useQuery({
    queryKey: ["rep_nabh_lifecycle", effectiveBranchId],
    queryFn: async () => {
      try {
        const pd1: any = { status: "Disposed", limit: 1 };
        const pd2: any = { status: "Condemned", limit: 1 };
        if (effectiveBranchId) { pd1.branch = effectiveBranchId; pd2.branch = effectiveBranchId; }
        
        const pac: any = { status: "Approved", limit: 2000 };
        const pdisp: any = { limit: 5000 };
        if (effectiveBranchId) { pac.branch = effectiveBranchId; pdisp.branch = effectiveBranchId; }

        const [rDisp, rCond, rAc, rAllDisp] = await Promise.all([
          apiList("/api/v1/assets/", pd1),
          apiList("/api/v1/assets/", pd2),
          apiList<{ asset_id: string }>("/api/v1/condemnation/requests/", pac),
          apiList<{ asset_id: string }>("/api/v1/disposals/", pdisp)
        ]);

        const disposedSet = new Set((rAllDisp.results ?? []).map((d) => d.asset_id));
        const pending = (rAc.results ?? []).filter((c) => !disposedSet.has(c.asset_id)).length;
        
        return { disposed: rDisp.count ?? 0, condemned: rCond.count ?? 0, pendingDisposal: pending };
      } catch (e) {
        return { disposed: 0, condemned: 0, pendingDisposal: 0 };
      }
    },
  });

  const exportRows = useMemo(() => () => [
    { Section: "Assets", Metric: "Active Assets", Value: av?.activeAssets ?? 0 },
    { Section: "Assets", Metric: "Condemned Assets", Value: lifecycle?.condemned ?? 0 },
    { Section: "Assets", Metric: "Disposed Assets", Value: lifecycle?.disposed ?? 0 },
    { Section: "Assets", Metric: "Pending Disposal", Value: lifecycle?.pendingDisposal ?? 0 },
    { Section: "Maintenance", Metric: "PM Compliance %", Value: pm?.compliance ?? 0 },
    { Section: "Maintenance", Metric: "PM Overdue", Value: pm?.overdue ?? 0 },
    { Section: "Calibration", Metric: "Calibrations Due (60d)", Value: calDue ?? 0 },
    { Section: "Contracts", Metric: "AMC Expiring (90d)", Value: amcExp ?? 0 },
    { Section: "Licences", Metric: "Licences Expiring (90d)", Value: licExp ?? 0 },
    { Section: "Audits", Metric: "Audits Completed", Value: audits?.completed ?? 0 },
    { Section: "Audits", Metric: "Audits Signed Off", Value: audits?.signed ?? 0 },
  ], [av, pm, calDue, amcExp, licExp, audits, lifecycle]);

  return (
    <ReportShell id="R-27" title="NABH Evidence Pack"
      description="Consolidated evidence: asset register, PM compliance, calibration, contracts, licences, audits, condemnation & disposal."
      filters={filters} onChange={setFilters} enabled={["branchId"]}
      exportRows={exportRows}>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard label="Active Assets" value={av?.activeAssets ?? 0} />
        <KpiCard label="PM Compliance" value={pm?.compliance == null ? "—" : `${pm.compliance}%`} tone={(pm?.compliance ?? 100) < 80 ? "warning" : "success"} />
        <KpiCard label="PM Overdue" value={pm?.overdue ?? 0} tone={(pm?.overdue ?? 0) > 0 ? "danger" : "default"} />
        <KpiCard label="Calibration Due (60d)" value={calDue ?? 0} />
        <KpiCard label="AMC Expiring (90d)" value={amcExp ?? 0} />
        <KpiCard label="Licences Expiring (90d)" value={licExp ?? 0} />
        <KpiCard label="Condemned Assets" value={lifecycle?.condemned ?? 0} />
        <KpiCard label="Pending Disposal" value={lifecycle?.pendingDisposal ?? 0} tone={(lifecycle?.pendingDisposal ?? 0) > 0 ? "warning" : "default"} />
        <KpiCard label="Disposed Assets" value={lifecycle?.disposed ?? 0} />
        <KpiCard label="Audits Completed" value={audits?.completed ?? 0} />
        <KpiCard label="Audits Signed Off" value={audits?.signed ?? 0} tone="success" />
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Evidence Index</CardTitle></CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p>This pack consolidates the artefacts required for NABH chapter on Facility Management & Safety (FMS) and Equipment Management.</p>
          <ul className="list-disc space-y-1 pl-6 text-muted-foreground">
            <li>Asset register — <a className="text-primary hover:underline" href="/reports/asset-register">R-01</a>.</li>
            <li>PM schedule and compliance — <a className="text-primary hover:underline" href="/reports/pm-compliance">R-03</a>.</li>
            <li>Calibration status — <a className="text-primary hover:underline" href="/reports/calibration-status">R-06</a>.</li>
            <li>Contracts register — <a className="text-primary hover:underline" href="/reports/amc-register">R-07</a>.</li>
            <li>Licence compliance — <a className="text-primary hover:underline" href="/reports/licence-compliance">R-16</a>.</li>
            <li>Condemnation approvals — <a className="text-primary hover:underline" href="/reports/condemnation-register">R-14 Condemnation Register</a>.</li>
            <li>Disposal records & certificates (incl. E-Waste, Biomedical) — <a className="text-primary hover:underline" href="/reports/disposal-register">R-15 Disposal Register</a>.</li>
            <li>Physical audits — <a className="text-primary hover:underline" href="/audits">Audits</a>.</li>
            <li>Audit trail — <a className="text-primary hover:underline" href="/audit-logs">Audit Logs</a>.</li>
          </ul>
        </CardContent>
      </Card>
    </ReportShell>
  );
}
